import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import GenericFormModal from '../GenericFormModal';
import { ReportGenerationModalProps } from './types';
import { useReportGenerationModal } from './useReportGenerationModal';
import FilenameInput from './FilenameInput/FilenameInput';
import ReportProgressModal from './ReportProgressModal/ReportProgressModal';

const ReportGenerationModal: React.FC<ReportGenerationModalProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  submitText = 'Generate Report',
  selectedPortfolios,
}) => {
  const {
    activeTab,
    setActiveTab,
    reportType,
    setReportType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    isInception,
    setIsInception,
    isLoading,
    isFromDateDisabled,
    filenameTemplate,
    setFilenameTemplate,
    handleSubmit,
    handleClose,
    processed,
    total,
    success,
    failed,
  } = useReportGenerationModal({ onSubmit, onClose, selectedPortfolios });

  return (
    <>
      <GenericFormModal
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
        title={title}
        submitText={submitText}
        submitDisabled={isLoading}
        submitLoading={isLoading}
        width="500px"
        maxWidth="500px"
        sx={{ height: 700 }}
        dataTestId="dialog-generate-report"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Holdings" value="holdings" />
            <Tab label="Reports" value="reports" />
          </Tabs>

          {/* Holdings Tab Content */}
          {activeTab === 'holdings' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={newValue => setFromDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
                format="DD-MM-YYYY"
                maxDate={dayjs()}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={newValue => setToDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
                minDate={fromDate || undefined}
                format="DD-MM-YYYY"
                maxDate={dayjs()}
              />
              <FilenameInput
                value={filenameTemplate}
                onChange={setFilenameTemplate}
                portfolios={selectedPortfolios || []}
                reportType="holdings"
                fromDate={fromDate?.format('DD-MMM-YYYY') || null}
                toDate={toDate?.format('DD-MMM-YYYY') || null}
              />
            </Box>
          )}

          {/* Reports Tab Content */}
          {activeTab === 'reports' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Report Type Selection */}
              <FormControl component="fieldset">
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, fontWeight: 500, color: 'text.primary' }}
                >
                  Report Type
                </FormLabel>
                <RadioGroup
                  value={reportType}
                  onChange={e => setReportType(e.target.value)}
                >
                  <FormControlLabel
                    value="highlights"
                    control={<Radio />}
                    label="Highlights"
                  />
                  <FormControlLabel
                    value="standard"
                    control={<Radio />}
                    label="Standard"
                  />
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label="Custom"
                  />
                  <FormControlLabel
                    value="custom_high_level"
                    control={<Radio />}
                    label="Custom (High Level)"
                  />
                  <FormControlLabel
                    value="builder"
                    control={<Radio />}
                    label="Builder"
                  />
                </RadioGroup>
              </FormControl>

              {/* Since Inception Checkbox */}
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isInception}
                      onChange={e => setIsInception(e.target.checked)}
                    />
                  }
                  label="Since Inception"
                />
              </FormControl>

              {/* Date Pickers */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={newValue => setFromDate(newValue)}
                  disabled={isFromDateDisabled}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: isFromDateDisabled
                        ? 'Disabled (Since Inception)'
                        : undefined,
                    },
                  }}
                  format="DD-MM-YYYY"
                  maxDate={dayjs()}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={newValue => setToDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                  minDate={fromDate || undefined}
                  format="DD-MM-YYYY"
                  maxDate={dayjs()}
                />
                <FilenameInput
                  value={filenameTemplate}
                  onChange={setFilenameTemplate}
                  portfolios={selectedPortfolios || []}
                  reportType={reportType}
                  fromDate={fromDate?.format('DD-MMM-YYYY') || null}
                  toDate={toDate?.format('DD-MMM-YYYY') || null}
                />
              </Box>
            </Box>
          )}

        </Box>
      </GenericFormModal>

      <ReportProgressModal
        open={isLoading && (selectedPortfolios?.length ?? 0) > 1}
        total={total}
        processed={processed}
        success={success}
        failed={failed}
      />
    </>
  );
};

export default ReportGenerationModal;
